// frontend/src/components/Chart/SeasonalTendency.jsx
//
// Seasonal tendency panes (day-of-week / month / quarter / day-of-month /
// week-of-year), always computed from DAILY history regardless of the chart
// timeframe. Rendered as the LEFT rail beside the main chart on xl screens
// (vertical stack) and as a wrapping row on smaller screens.
import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { loadExtendedMarketData } from '../../services/dataManager';
import { computeSeasonalStats } from '../../utils/seasonality';
import InfoHint from '../Common/InfoHint';

const tooltipStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  color: '#111827',
  fontSize: '12px',
};

const SeasonalTendency = ({ symbol }) => {
  const [seasonal, setSeasonal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSeasonal(null);
    loadExtendedMarketData(symbol, '1D', { maxDataPoints: 2000 })
      .then((res) => {
        if (cancelled || !res?.candles) return;
        setSeasonal(computeSeasonalStats(res.candles));
      })
      .catch(() => { /* rail simply stays hidden */ });
    return () => { cancelled = true; };
  }, [symbol]);

  if (!seasonal) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-start gap-1"><h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Seasonal Tendency</h4><InfoHint title="Seasonal tendency" align="left">Average daily return grouped by calendar bucket (day of week, month, quarter, …), from daily history. Green = that bucket averaged positive, red = negative, blue = the bucket we're in now. The % is how often the current bucket closed up. A tendency, not a guarantee.</InfoHint></span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 xl:hidden">
          avg daily return · blue = now
        </span>
      </div>
      {/* Row that wraps on small screens; vertical stack in the xl rail.
          flex-1 on the panes shares the rail height so the column lines up
          with the tall chart beside it. */}
      <div className="flex flex-wrap gap-3 xl:flex-col xl:flex-nowrap xl:flex-1">
        {[seasonal.dayOfWeek, seasonal.month, seasonal.quarter, seasonal.dayOfMonth, seasonal.weekOfYear].map((group) => (
          <SeasonalPane key={group.title} group={group} />
        ))}
      </div>
    </div>
  );
};

const SeasonalPane = ({ group }) => {
  const now = group.data.find((b) => b.isCurrent);
  const winPct = now && now.count > 0 ? Math.round(now.winRate * 100) : null;
  return (
    <div className="flex-1 min-w-[150px] xl:min-w-0 bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
      <div className="flex items-center justify-between mb-1">
        <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400">{group.title}</h5>
        {winPct !== null && (
          <span
            className="text-[11px] font-medium text-gray-500 dark:text-gray-400 cursor-help"
            title={`${now.label}: ${winPct}% of these ${group.title.toLowerCase()} days closed up (n=${now.count})`}
          >
            {winPct}%
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={90}>
        <BarChart data={group.data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <XAxis dataKey="label" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
          <YAxis hide />
          <Tooltip
            formatter={(v, name, { payload }) => [
              `${v.toFixed(3)}% avg · ${Math.round(payload.winRate * 100)}% win · n=${payload.count}`,
              payload.label,
            ]}
            contentStyle={tooltipStyle}
          />
          <ReferenceLine y={0} stroke="#6B7280" strokeWidth={1} />
          <Bar dataKey="avg" isAnimationActive={false}>
            {group.data.map((entry) => (
              <Cell
                key={entry.label}
                fill={entry.isCurrent ? '#3B82F6' : entry.avg >= 0 ? '#22c55e' : '#ef4444'}
                fillOpacity={entry.isCurrent ? 1 : 0.55}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SeasonalTendency;
